import { triggerPostMoveFlash } from "@atlaskit/pragmatic-drag-and-drop-flourish/trigger-post-move-flash";
import { extractClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { reorderWithEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/util/reorder-with-edge";
import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { Box, Typography, useTheme } from "@mui/material";
import { ReactElement, useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { LayoutItem } from "../../models/types";
import { ListItem } from "./ListItem";

type ListArrangementProps = {
  title?: string;
  rightComponent?: React.ReactNode;
  listItems: any[];
  updateLayout: (layout: any[]) => void;
  onRemoveItem?: (idx: number) => void;
  onHideItem?: (idx: number) => void;
  border?: boolean;
  renderItem?: (item: any, idx: any) => React.ReactNode;
};

export const ListArrangement = (props: ListArrangementProps) => {
  const {
    title,
    rightComponent,
    listItems,
    updateLayout,
    onRemoveItem,
    onHideItem,
    border = true,
    renderItem,
  } = props;
  const theme = useTheme();
  const [items, setItems] = useState<any[]>(listItems);

  useEffect(() => {
    return monitorForElements({
      canMonitor({ source }) {
        return true;
      },
      onDrop({ location, source }) {
        const target = location.current.dropTargets[0];
        if (!target) {
          return;
        }

        const sourceData = source.data;
        const targetData = target.data;

        const indexOfSource = items.findIndex(
          (item) => (item.key ?? item.ref) === (sourceData.key ?? sourceData.ref)
        );
        const indexOfTarget = items.findIndex(
          (item) => (item.key ?? item.ref) === (targetData.key ?? targetData.ref)
        );

        if (indexOfTarget < 0 || indexOfSource < 0) {
          return;
        }

        const closestEdgeOfTarget = extractClosestEdge(targetData);

        // Using `flushSync` so we can query the DOM straight after this line
        flushSync(() => {
          const ntasks = reorderWithEdge({
            list: items,
            startIndex: indexOfSource,
            indexOfTarget,
            closestEdgeOfTarget,
            axis: "vertical",
          });
          setItems(ntasks);
          updateLayout(ntasks);
        });
        // Being simple and just querying for the task after the drop.
        // We could use react context to register the element in a lookup,
        // and then we could retrieve that element after the drop and use
        // `triggerPostMoveFlash`. But this gets the job done.
        const element = document.querySelector(
          `${sourceData.type}__${sourceData.key ?? sourceData.ref}`
        );
        if (element instanceof HTMLElement) {
          triggerPostMoveFlash(element);
        }
      },
    });
  }, [items]);

  useEffect(() => {
    setItems(listItems);
  }, [listItems]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="16px"
      padding={border ? "8px" : "0px"}
      border={border ? `1px solid ${theme.palette.custom.primaryBorder}` : "none"}
      borderRadius="3px"
    >
      {title && (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h3">{title}</Typography>
          {rightComponent}
        </Box>
      )}
      <Box display="flex" flexDirection="column" gap="8px">
        {items.map((item, idx) => {
          const isObjectKey = item.type === "object_key";
          return (
            <ListItem
              item={item}
              onClick={() =>
                isObjectKey && onHideItem
                  ? onHideItem(idx)
                  : onRemoveItem
                  ? onRemoveItem(idx)
                  : () => {}
              }
              renderItem={renderItem ? renderItem(item, idx) : null}
            />
          );
        })}
      </Box>
    </Box>
  );
};
